import {
  IonContent,
  IonHeader,
  IonToolbar,
  IonFooter,
  IonTitle,
} from "@ionic/react";
import React, { useRef, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { isSafari } from "react-device-detect";
import { AnimatePresence, motion } from "framer-motion";
import { backwardVariants, forwardVariants } from "../../../Routes/AnimatedPage";
import ArrowBackIcon from "../../../assets/imgs/on-boardings-arrow.svg";
import rightIcon from "../../../assets/imgs/right-icon.svg";
import SelectableOption from "./SelectableOption";
import { OnboardingStep } from "../data/onboardingSteps";
import { useKeyboardHandler } from "../../../hooks/useKeyboardHandler";
import ProgressIndicator from "./ProgressIndicator";

interface OnboardingContentProps {
  step: OnboardingStep;
  selectedOptions: number[];
  onOptionSelect: (index: number) => void;
  onBack: () => void;
  onContinue: () => void;
  canContinue: boolean;
  isOtherOptionSelected?: boolean;
  textAreaValue?: string;
  onTextAreaChange?: (value: string) => void;
  currentStep: number;
  totalSteps: number;
  direction: "forward" | "backward";
}

const OnboardingContent: React.FC<OnboardingContentProps> = ({
  step,
  selectedOptions,
  onOptionSelect,
  onBack,
  onContinue,
  canContinue,
  isOtherOptionSelected = false,
  textAreaValue = "",
  onTextAreaChange,
  currentStep,
  totalSteps,
  direction,
}) => {
  console.log(textAreaValue, "textAreaValue");

  // Create ref for textarea to enable keyboard handling
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const Platform = Capacitor.getPlatform();

  // Use keyboard handler hook for automatic scroll behavior
  const { isKeyboardOpen, keyboardHeight } = useKeyboardHandler({
    elementRef: textareaRef,
    scrollIntoView: true,
    scrollDelay: 100, // Reduced delay for better responsiveness
    scrollBehavior: "smooth",
    scrollBlock: "center",
  });

  // Auto-focus textarea when "Other" option is selected
  useEffect(() => {
    if (isOtherOptionSelected && textareaRef.current) {
      // Small delay to ensure the textarea is rendered and visible
      const timeoutId = setTimeout(() => {
        textareaRef.current?.focus();
      }, 150);

      return () => clearTimeout(timeoutId);
    }
  }, [isOtherOptionSelected]);

  return (
    <div className="h-screen flex flex-col">
      <IonHeader className="ion-no-border modal-ion-header">
        <IonToolbar className="custom-toolbar">
          <div className="p-3 flex justify-between items-center">
            {step.showBackButton ? (
              <div
                className="cursor-pointer rounded-[100px] w-[40px] h-[40px] flex justify-center items-center bg-white/10"
                onClick={onBack}
              >
                <img src={ArrowBackIcon} alt="back" />
              </div>
            ) : (
              <div className="w-[40px] h-[40px]" /> // Placeholder to maintain layout
            )}

            {/* Progress Indicator */}
            <IonTitle>
              <ProgressIndicator
                totalSteps={totalSteps}
                currentStep={currentStep}
              />
            </IonTitle>
          </div>
        </IonToolbar>
      </IonHeader>

      <IonContent
        className="custom-content "
        scrollY={true}
        style={
          {
            // Adjust content padding when keyboard is open
            ...(isKeyboardOpen &&
              keyboardHeight > 0 && {
                "--padding-bottom": `${keyboardHeight}px`,
              }),
          } as React.CSSProperties
        }
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={step.id}
            initial="initial"
            animate="in"
            exit="exit"
            variants={
              direction === "forward" ? forwardVariants : backwardVariants
            }
            className="h-full"
          >
            <div className="px-[12px]">
              {/* Header Section */}
              <div className="flex-shrink-0 pt-4 pb-2">
                <div className="flex items-center justify-center w-full">
                  <span className="text-[26px] text-white font-semibold text-center">
                    {step.title}
                  </span>
                </div>
              </div>

              {/* Options Section */}
              <div className="mt-4 space-y-[10px] pb-4">
                {step.options.map((option, index) => (
                  <SelectableOption
                    key={index}
                    icon={option.icon}
                    iconAlt={option.iconAlt || "option"}
                    text={option.text}
                    rightIcon={rightIcon}
                    isSelected={selectedOptions.includes(index)}
                    onClick={() => onOptionSelect(index)}
                  />
                ))}

                {/* Textarea Section - Shows when "Other" option is selected */}
                {isOtherOptionSelected && (
                  <div className="mt-[20px]">
                    <textarea
                      ref={textareaRef}
                      value={textAreaValue}
                      onChange={(e) => onTextAreaChange?.(e.target.value)}
                      placeholder="Please explain further ..."
                      className="w-full h-[120px] p-4 rounded-[20px] bg-[#0084FF1A] border-[2px] border-[#00E0FF33] text-white placeholder-white/50 resize-none focus:outline-none focus:border-[#00E0FF] focus:border-[2px] text-[12px] font-medium"
                      style={{
                        fontFamily: "inherit",
                        lineHeight: "1.5",
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </IonContent>

      {/* Continue Button in Footer */}
      <IonFooter className="ion-no-border">
        <div
          className="bg-gradient-to-t from-black via-black/90 to-transparent pt-4"
          style={{
            paddingBottom:
              Platform == "web"
                ? isSafari
                  ? "120px"
                  : "90px"
                : "calc(env(safe-area-inset-bottom) + 20px)",
          }}
        >
          <div className="px-[16px]">
            <button
              className={`h-[60px] w-full flex justify-center items-center rounded-[50.13px] ${
                canContinue
                  ? "bg-[#00E0FF] cursor-pointer"
                  : "bg-[#00E0FF]/50 cursor-not-allowed"
              }`}
              onClick={canContinue ? onContinue : undefined}
              disabled={!canContinue}
            >
              <span className="text-black text-[14px] font-medium">
                Continue
              </span>
            </button>
          </div>
        </div>
      </IonFooter>
    </div>
  );
};

export default OnboardingContent;
